import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Search, MapPin, Video, Calendar, Clock, Filter, X, ChevronDown, ChevronUp, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DateFilter from '../../components/DateFilter';
import Dropdown from '../../components/Dropdown';
import { formatAppDate } from '../../utils/dateUtils';

const AttendanceList = () => {
  const { user } = useAuth();
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(new Date());
  const [userFilter, setUserFilter] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedActivitySlot, setSelectedActivitySlot] = React.useState(null);

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, [dateFilter, userFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let url = '/attendance/list';
      const params = new URLSearchParams();
      if (dateFilter) {
        const d = dateFilter;
        const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        params.append('date', formattedDate);
      }
      if (userFilter) params.append('userId', userFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await api.get(url);
      setAttendances(res.data.data || res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/auth/users?limit=100&page=1');
      const usersData = res.data.data || res.data || [];
      const filteredUsers = usersData.filter(u => {
        const roleName = u.role?.name || u.role;
        return roleName === 'Marketing Agent' || roleName === 'Delivery Boy';
      });
      const userOptions = filteredUsers.map(u => ({
        value: u.id,
        label: `${u.name} (${u.role?.name || u.role})`
      }));
      setUsersList([{ value: '', label: 'All Users' }, ...userOptions]);
    } catch (err) {
      console.error(err);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateTotalHours = (start, end, breakMinutes = 0) => {
    if (!start) return '-';
    const endTime = end ? new Date(end) : new Date();
    const diff = endTime - new Date(start);
    if (diff <= 0) return '-';

    // Subtract break minutes
    const breakMs = (breakMinutes || 0) * 60 * 1000;
    const activeMs = diff - breakMs;
    if (activeMs <= 0) return '-';

    const hours = Math.floor(activeMs / (1000 * 60 * 60));
    const minutes = Math.floor((activeMs % (1000 * 60 * 60)) / (1000 * 60));

    let res = `${hours}h ${minutes}m`;
    if (!end) {
      res += ' (Ongoing)';
    }
    return res;
  };

  const formatBreakTime = (breakMinutes) => {
    if (!breakMinutes) return '0m';
    const hours = Math.floor(breakMinutes / 60);
    const minutes = breakMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const clearFilters = () => {
    setDateFilter(null);
    setUserFilter('');
  };

  const openMap = (lat, lng) => {
    if (lat && lng) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    }
  };

  const renderMapLink = (lat, lng, label) => {
    if (!lat || !lng) return <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic' }}>No Location</span>;
    return (
      <button
        onClick={() => openMap(lat, lng)}
        style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        title={label}
      >
        <MapPin size={12} /> View Location
      </button>
    );
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Attendance Log</h1>
          <p style={{ color: '#6b7280' }}>View team check-ins and check-outs</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>

          <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Filter by Date</label>
            <DatePicker
              selected={dateFilter}
              onChange={(date) => setDateFilter(date)}
              dateFormat="dd/MM/yyyy"
              className="input-field"
              wrapperClassName="custom-datepicker-wrapper"
              placeholderText="dd/mm/yyyy"
              style={{ width: '100%', padding: '0.6rem 1rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}
            />
          </div>

          <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>Filter by User</label>
            <Dropdown
              value={userFilter}
              onChange={(val) => setUserFilter(val)}
              options={usersList.length > 0 ? usersList : [{ value: '', label: 'All Users' }]}
              placeholder="All Users"
              style={{ border: '1px solid var(--color-border)', borderRadius: '8px' }}
            />
          </div>

          {(dateFilter || userFilter) && (
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
              style={{ padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <X size={16} /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="card table-container">
        <table className="table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px', marginTop: 10 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Date</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Employee</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Check-In</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Check-Out</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Break Time</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Total Hours</th>
              <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Status</th>
              <th style={{ padding: '0.65rem 0.75rem', width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading attendance data...</td>
              </tr>
            ) : attendances.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No attendance records found.</td>
              </tr>
            ) : (
              attendances.map(record => (
                <React.Fragment key={record.id}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: expandedId === record.id ? '#f8fafc' : 'white' }}>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontSize: '0.825rem' }}>
                      <div style={{ fontWeight: '600', color: '#1f2937' }}>{formatDate(record.date)}</div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontSize: '0.825rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', color: 'var(--color-primary)', backgroundColor: 'var(--color-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.85rem' }}>
                          {record.user?.name ? record.user.name.charAt(0) : 'U'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>{record.user?.name || 'Unknown'}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{record.user?.role?.name || 'User'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontSize: '0.825rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: '600', color: '#374151' }}>
                          <Clock size={13} color="#9ca3af" />
                          {formatTime(record.startDayTime)}
                        </div>
                        {renderMapLink(record.startDayLat, record.startDayLng, 'Check-In Location')}
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontSize: '0.825rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: '600', color: '#374151' }}>
                          <Clock size={13} color="#9ca3af" />
                          {formatTime(record.endDayTime)}
                        </div>
                        {renderMapLink(record.endDayLat, record.endDayLng, 'Check-Out Location')}
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontWeight: '500', color: '#4b5563', fontSize: '0.825rem' }}>
                      {formatBreakTime(record.totalBreakMinutes)}
                      {record.currentBreakStartTime && <span style={{ marginLeft: '0.5rem', color: '#f59e0b', fontSize: '0.75rem' }}>(On Break)</span>}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', fontWeight: '600', color: '#059669', fontSize: '0.825rem' }}>
                      {calculateTotalHours(record.startDayTime, record.endDayTime, record.totalBreakMinutes)}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle' }}>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: record.status === 'COMPLETED' ? '#dcfce7' : record.status === 'STARTED' ? '#dbeafe' : '#f3f4f6',
                        color: record.status === 'COMPLETED' ? '#166534' : record.status === 'STARTED' ? '#1e40af' : '#374151'
                      }}>
                        {record.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', verticalAlign: 'middle', textAlign: 'center' }}>
                      <button
                        onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#6b7280' }}
                      >
                        {expandedId === record.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === record.id && (
                    <tr key={`expanded-${record.id}`} style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--color-border)' }}>
                      <td colSpan="8" style={{ padding: '1.5rem' }}>
                        <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#475569', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Activity size={16} /> Activity Timeline
                        </h4>
                        {(!record.activities || record.activities.length === 0) ? (
                          <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                            No activity recorded for this day.
                          </p>
                        ) : (
                          (() => {
                            const SLOT_DURATION = 1;

                            const getSlotStart = (date) => {
                              const hour = date.getHours();

                              // 1-3, 3-5, 5-7, etc.
                              const slotStart = Math.floor((hour - 1) / SLOT_DURATION) * SLOT_DURATION + 1;

                              const result = new Date(date);
                              result.setHours(slotStart, 0, 0, 0);

                              return result;
                            };

                            const getSlotKey = (date) => {
                              const slotStart = getSlotStart(date);

                              return `${slotStart.getFullYear()}-${slotStart.getMonth()}-${slotStart.getDate()}-${slotStart.getHours()}`;
                            };

                            const formatSlotTime = (date) => {
                              return date.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            };

                            const getSlotEnd = (date) => {
                              const end = new Date(date);
                              end.setHours(end.getHours() + SLOT_DURATION);

                              return end;
                            };

                            // Group activities into 2-hour slots
                            const groupedActivities = record.activities.reduce((groups, act) => {
                              const date = new Date(act.time);

                              if (Number.isNaN(date.getTime())) {
                                return groups;
                              }

                              const slotStart = getSlotStart(date);
                              const slotKey = getSlotKey(date);

                              if (!groups[slotKey]) {
                                groups[slotKey] = {
                                  key: slotKey,
                                  start: slotStart,
                                  end: getSlotEnd(slotStart),
                                  activities: []
                                };
                              }

                              groups[slotKey].activities.push(act);

                              return groups;
                            }, {});

                            const slotGroups = Object.values(groupedActivities).sort(
                              (a, b) => b.start.getTime() - a.start.getTime()
                            );

                            // Latest slot selected by default
                            const activeSlot =
                              slotGroups.find(
                                slot => slot.key === selectedActivitySlot
                              ) || slotGroups[0];

                            return (
                              <div>

                                {/* ================= TIME SLOTS ================= */}
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: '0.6rem',
                                    overflowX: 'auto',
                                    paddingBottom: '0.75rem',
                                    scrollbarWidth: 'thin'
                                  }}
                                >
                                  {slotGroups.map((slot) => {
                                    const isSelected = activeSlot?.key === slot.key;

                                    return (
                                      <button
                                        key={slot.key}
                                        type="button"
                                        onClick={() => setSelectedActivitySlot(slot.key)}
                                        style={{
                                          minWidth: '145px',
                                          padding: '0.65rem 0.8rem',
                                          borderRadius: '9px',
                                          border: isSelected
                                            ? '1px solid var(--color-primary)'
                                            : '1px solid #e2e8f0',
                                          backgroundColor: isSelected
                                            ? '#eff6ff'
                                            : '#fff',
                                          cursor: 'pointer',
                                          textAlign: 'left',
                                          flexShrink: 0,
                                          transition: 'all 0.15s ease'
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '700',
                                            color: isSelected
                                              ? 'var(--color-primary)'
                                              : '#334155'
                                          }}
                                        >
                                          {formatSlotTime(slot.start)}
                                          {' – '}
                                          {formatSlotTime(slot.end)}
                                        </div>

                                        <div
                                          style={{
                                            fontSize: '0.72rem',
                                            color: '#94a3b8',
                                            marginTop: '3px'
                                          }}
                                        >
                                          {slot.activities.length}{' '}
                                          {slot.activities.length === 1
                                            ? 'activity'
                                            : 'activities'}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>


                                {/* ================= SELECTED SLOT ================= */}
                                {activeSlot && (
                                  <div style={{ marginTop: '0.75rem' }}>

                                    {/* Slot Header */}
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.6rem',
                                        padding: '0 0.25rem'
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: '0.82rem',
                                          fontWeight: '600',
                                          color: '#64748b'
                                        }}
                                      >
                                        Activities from {formatSlotTime(activeSlot.start)}
                                        {' – '}
                                        {formatSlotTime(activeSlot.end)}
                                      </div>

                                      <div
                                        style={{
                                          fontSize: '0.72rem',
                                          color: '#94a3b8'
                                        }}
                                      >
                                        {activeSlot.activities.length}{' '}
                                        {activeSlot.activities.length === 1
                                          ? 'activity'
                                          : 'activities'}
                                      </div>
                                    </div>


                                    {/* Activities */}
                                    <div
                                      style={{
                                        position: 'relative',
                                        paddingLeft: '0.25rem'
                                      }}
                                    >
                                      {/* Timeline */}
                                      <div
                                        style={{
                                          position: 'absolute',
                                          left: '13px',
                                          top: '10px',
                                          bottom: '10px',
                                          width: '2px',
                                          backgroundColor: '#e2e8f0'
                                        }}
                                      />

                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '0.4rem'
                                        }}
                                      >
                                        {activeSlot.activities.map((act, i) => {

                                          const hasLocation =
                                            act.latitude !== null &&
                                            act.latitude !== undefined &&
                                            act.latitude !== '' &&
                                            act.longitude !== null &&
                                            act.longitude !== undefined &&
                                            act.longitude !== '';

                                          return (
                                            <div
                                              key={i}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.7rem',
                                                position: 'relative',
                                                zIndex: 1
                                              }}
                                            >
                                              {/* Timeline Dot */}
                                              <div
                                                style={{
                                                  width: '28px',
                                                  height: '28px',
                                                  minWidth: '28px',
                                                  borderRadius: '50%',
                                                  backgroundColor: '#fff',
                                                  border: '2px solid var(--color-primary)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center'
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    backgroundColor: 'var(--color-primary)'
                                                  }}
                                                />
                                              </div>

                                              {/* Activity Row */}
                                              <div
                                                style={{
                                                  flex: 1,
                                                  minWidth: 0,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  gap: '0.75rem',
                                                  backgroundColor: '#fff',
                                                  border: '1px solid #e2e8f0',
                                                  borderRadius: '7px',
                                                  padding: '0.5rem 0.7rem'
                                                }}
                                              >
                                                {/* Title + Time */}
                                                <div
                                                  style={{
                                                    minWidth: 0,
                                                    flex: 1
                                                  }}
                                                >
                                                  {/* Title */}
                                                  <p
                                                    style={{
                                                      margin: 0,
                                                      fontSize: '0.82rem',
                                                      color: '#334155',
                                                      fontWeight: '600',
                                                      lineHeight: '1.35',
                                                      whiteSpace: 'nowrap',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis'
                                                    }}
                                                    title={act.title}
                                                  >
                                                    {act.title}
                                                  </p>

                                                  {/* Time - Second Row */}
                                                  <p
                                                    style={{
                                                      margin: '3px 0 0',
                                                      fontSize: '0.68rem',
                                                      color: '#94a3b8',
                                                      lineHeight: '1'
                                                    }}
                                                  >
                                                    {formatTime(act.time)}
                                                  </p>
                                                </div>

                                                {/* Google Maps */}
                                                {hasLocation && (
                                                  <a
                                                    href={`https://www.google.com/maps?q=${act.latitude},${act.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    title="Open location in Google Maps"
                                                    style={{
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      gap: '0.25rem',
                                                      padding: '0.3rem 0.5rem',
                                                      borderRadius: '5px',
                                                      fontSize: '0.68rem',
                                                      fontWeight: '600',
                                                      backgroundColor: '#059669',
                                                      color: '#fff',
                                                      textDecoration: 'none',
                                                      whiteSpace: 'nowrap',
                                                      flexShrink: 0
                                                    }}
                                                  >
                                                    <MapPin size={12} />
                                                    Maps ↗
                                                  </a>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceList;
